import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiGet, apiPost } from "../api/client";
import type { ScopeRepairChanged, ScopeRepairJob, ScopeRepairLastResponse, ScopeRepairLastRun, ScopeRepairPreview } from "../api/types";
import {
  POLL_RETRY_LIMIT, applyStages, canApply, canUndo, changedSummary, formatClock, formatCount, isTransientPollError,
  lastRunStamp, nothingToChange, previewTableRows, recomputeLabel, refusalLabel, runTag, scopeTableRows,
} from "../lib/scopeRepair";

const BASE = "/api/admin/backbone/scope-repair";
const POLL_MS = 1000;

type View = "idle" | "previewing" | "preview" | "applying" | "done" | "undoing" | "undone";

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Polls a job until it stops running. A dropped connection or server error is retried a bounded number of times. */
async function waitForJob(id: string, onTick: (job: ScopeRepairJob) => void, cancelled: () => boolean): Promise<ScopeRepairJob> {
  let failures = 0;
  for (;;) {
    let job: ScopeRepairJob;
    try {
      job = await apiGet<ScopeRepairJob>(`${BASE}/jobs/${id}`);
      failures = 0;
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      failures += 1;
      if (!isTransientPollError(status) || failures > POLL_RETRY_LIMIT) throw error;
      await sleep(POLL_MS);
      continue;
    }
    if (cancelled()) return job;
    onTick(job);
    if (job.state !== "running") return job;
    await sleep(POLL_MS);
  }
}

export function ScopeRepairPanel() {
  const [view, setView] = useState<View>("idle");
  const [last, setLast] = useState<ScopeRepairLastRun | null>(null);
  const [preview, setPreview] = useState<ScopeRepairPreview | null>(null);
  const [job, setJob] = useState<ScopeRepairJob | null>(null);
  const [changed, setChanged] = useState<ScopeRepairChanged | null>(null);
  const [recompute, setRecompute] = useState<string | undefined>(undefined);
  const [restored, setRestored] = useState<number | null>(null);
  const [skipped, setSkipped] = useState<number | null>(null);
  const [undoRecompute, setUndoRecompute] = useState<string | undefined>(undefined);
  const [stale, setStale] = useState(false);
  const [problem, setProblem] = useState<{ kind: "stale" | "message"; text: string } | null>(null);
  const alive = useRef(true);
  const cancelled = useCallback(() => !alive.current, []);

  const loadLast = useCallback(async (): Promise<ScopeRepairLastResponse | null> => {
    try {
      const response = await apiGet<ScopeRepairLastResponse>(`${BASE}/last`);
      if (alive.current) setLast(response.last);
      return response;
    } catch {
      /* Not an admin, or the pane is offline: the controls simply stay at their idle state. */
      return null;
    }
  }, []);

  const busy = view === "previewing" || view === "applying" || view === "undoing";

  function fail(error: unknown) {
    const message = error instanceof Error ? error.message : "";
    const label = refusalLabel(error instanceof ApiError ? error.status : undefined, message);
    setProblem(label);
    if (label.kind === "stale") setStale(true);
  }

  /** A job that ended in error: scope-name changes are a stale preview, anything else is a plain failure. */
  function jobFailed(finished: ScopeRepairJob) {
    const text = finished.error ?? "";
    setProblem(/scope names changed|undo unfinished/i.test(text) ? refusalLabel(409, text) : { kind: "message", text: "Failed" });
  }

  async function followPreview(id: string) {
    try {
      const finished = await waitForJob(id, (j) => alive.current && setJob(j), cancelled);
      if (!alive.current) return;
      if (finished.state !== "done" || !finished.result) throw new Error(finished.error ?? "Failed");
      setPreview(finished.result as ScopeRepairPreview);
      setView("preview");
    } catch (error) {
      if (!alive.current) return;
      fail(error);
      setView("idle");
    }
  }

  async function followApply(id: string) {
    try {
      const finished = await waitForJob(id, (j) => alive.current && setJob(j), cancelled);
      if (!alive.current) return;
      if (finished.state !== "done") {
        // The run stays on record as interrupted: show it with Resume and Undo instead of the old preview.
        jobFailed(finished);
        setPreview(null);
        setView("idle");
        await loadLast();
        return;
      }
      const result = finished.result as { changed?: ScopeRepairChanged; recompute?: string } | null;
      setChanged(result?.changed ?? null);
      setRecompute(result?.recompute);
      setPreview(null);
      setView("done");
      void loadLast();
    } catch (error) {
      if (!alive.current) return;
      fail(error);
      setPreview(null);
      setView("idle");
      void loadLast();
    }
  }

  async function followUndo(id: string) {
    try {
      const finished = await waitForJob(id, (j) => alive.current && setJob(j), cancelled);
      if (!alive.current) return;
      if (finished.state !== "done") throw new Error(finished.error ?? "Failed");
      const result = finished.result as { restored?: number; skipped?: number; recompute?: string } | null;
      setRestored(result?.restored ?? 0);
      setSkipped(result?.skipped ?? 0);
      setUndoRecompute(result?.recompute);
      setView("undone");
      void loadLast();
    } catch (error) {
      if (!alive.current) return;
      fail(error);
      setView("idle");
      void loadLast();
    }
  }

  useEffect(() => {
    alive.current = true;
    void (async () => {
      const response = await loadLast();
      const active = response?.active;
      if (!alive.current || !active) return;
      // A job is already running (page reloaded or another admin started it): show its progress.
      setJob(active);
      if (active.kind === "apply") { setView("applying"); void followApply(active.id); }
      else if (active.kind === "undo") { setView("undoing"); void followUndo(active.id); }
      else { setView("previewing"); void followPreview(active.id); }
    })();
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runPreview() {
    setProblem(null);
    setStale(false);
    setView("previewing");
    setJob(null);
    try {
      const started = await apiPost<ScopeRepairJob>(`${BASE}/preview`);
      await followPreview(started.id);
    } catch (error) {
      if (!alive.current) return;
      fail(error);
      setView("idle");
    }
  }

  async function runApply() {
    if (!preview) return;
    setProblem(null);
    setView("applying");
    setJob(null);
    try {
      const started = await apiPost<ScopeRepairJob>(`${BASE}/apply`, { preview_id: preview.preview_id });
      await followApply(started.id);
    } catch (error) {
      if (!alive.current) return;
      // Refused before it started (stale preview, already running): stay on the preview, Apply disabled when stale.
      fail(error);
      setView("preview");
      void loadLast();
    }
  }

  async function runUndo() {
    if (!last) return;
    setProblem(null);
    setView("undoing");
    setJob(null);
    try {
      const started = await apiPost<ScopeRepairJob>(`${BASE}/undo`, { run_id: last.run_id });
      await followUndo(started.id);
    } catch (error) {
      if (!alive.current) return;
      fail(error);
      setView("idle");
      void loadLast();
    }
  }

  const stages = applyStages(job?.progress ?? {});
  const showUndo = canUndo(last, busy) && (view === "idle" || view === "done");
  const tag = runTag(last?.state);

  return (
    <div className="scope-repair" aria-label="History">
      <h4 className="scope-repair-title">History</h4>

      <div className="scope-repair-bar">
        <button type="button" className="settings-save-btn" title="Re-checks stored packets tagged no scope or ambiguous against the current Scopes list" onClick={() => void runPreview()} disabled={busy}>
          Recheck History
        </button>
        {last && (last.finished_at || last.started_at) && (
          <span className="scope-repair-last">Last Run: {lastRunStamp(last)}</span>
        )}
        {tag && view === "idle" && <span className="scope-repair-tag">{tag}</span>}
        {tag && view === "idle" && (
          <button type="button" className="scope-list-reset" onClick={() => void runPreview()}>Resume</button>
        )}
        {showUndo && (
          <button type="button" className="scope-list-reset" onClick={() => void runUndo()}>Undo</button>
        )}
      </div>

      {problem && (
        <p className="scope-list-error-text" role="alert">
          {problem.text}
          {problem.kind === "stale" && (
            <> <button type="button" className="scope-list-link-button" onClick={() => void runPreview()}>Preview Again</button></>
          )}
        </p>
      )}

      {view === "previewing" && <p className="scope-repair-status" role="status" aria-live="polite">Counting</p>}

      {view === "preview" && preview && (
        <div className="scope-repair-preview">
          <h5>Rows That Would Change</h5>
          <div className="scope-repair-table-wrap">
            <table className="scope-repair-table">
              <thead><tr><th>Table</th><th>Checked</th><th>Change</th><th title="Transport code could not be read">Unreadable</th></tr></thead>
              <tbody>
                {previewTableRows(preview).map((row) => (
                  <tr key={row.label}><th scope="row">{row.label}</th><td>{row.checked}</td><td>{row.change}</td><td>{row.unreadable}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {scopeTableRows(preview).length > 0 && (
            <div className="scope-repair-table-wrap">
              <table className="scope-repair-table">
                <thead><tr><th>Scope</th><th>Packets</th><th>CoreScope</th><th>Link Events</th></tr></thead>
                <tbody>
                  {scopeTableRows(preview).map((row) => (
                    <tr key={row.scope}><th scope="row">{row.scope}</th><td>{row.packets}</td><td>{row.corescope}</td><td>{row.linkEvents}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="scope-repair-meta">Scope Names: {formatCount(preview.candidate_count)} <span className="scope-repair-gap" /> Counted: {formatClock(preview.computed_at)}</p>
          <div className="scope-repair-actions">
            <button type="button" className="settings-save-btn" onClick={() => void runApply()} disabled={!canApply(preview, busy || stale)}
              title={nothingToChange(preview) ? "Nothing To Change" : undefined}>Apply</button>
            <button type="button" className="scope-list-reset" onClick={() => { setPreview(null); setProblem(null); setStale(false); setView("idle"); }}>Cancel</button>
            {nothingToChange(preview) && <span className="scope-repair-meta">Nothing To Change</span>}
          </div>
        </div>
      )}

      {view === "applying" && (
        <div className="scope-repair-progress" role="status" aria-live="polite">
          <strong>Applying</strong>
          {[["Packets", stages.packets], ["CoreScope Packets", stages.corescope], ["Link Events", stages.linkEvents]].map(([label, value]) => (
            <div className="scope-repair-bar-row" key={label as string}>
              <span>{label} {value}%</span>
              <progress max={100} value={value as number} aria-label={label as string} />
            </div>
          ))}
        </div>
      )}

      {view === "done" && (
        <div className="scope-repair-result" role="status">
          <strong>Done</strong>
          {changed && <span>Changed: {changedSummary(changed)}</span>}
          <span>Audit Recompute: {recomputeLabel(recompute)}</span>
        </div>
      )}

      {view === "undoing" && <p className="scope-repair-status" role="status" aria-live="polite">Undoing</p>}

      {view === "undone" && (
        <div className="scope-repair-result" role="status">
          <strong>Undone</strong>
          <span>Restored: {formatCount(restored ?? 0)} Rows</span>
          <span>Skipped: {formatCount(skipped ?? 0)}</span>
          <span>Audit Recompute: {recomputeLabel(undoRecompute)}</span>
        </div>
      )}
    </div>
  );
}
